/**
 * CalendarHeader Component
 * Navigation header with prev/next arrows, date display, and view toggle.
 * Built with Tailwind CSS and shadcn/ui components.
 */

import { useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useCalendarStore, CalendarView } from '@/stores/calendarStore';
import { useCampaignStore } from '@/stores/campaignStore';
import { getTimezoneAbbreviation } from '@/lib/calendarUtils';

/** Check if the campaign timezone differs from the browser's local timezone */
function isDifferentFromLocal(timeZoneId?: string): boolean {
  if (!timeZoneId) return false;
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return localTz !== timeZoneId;
}

/**
 * Format the visible date for display based on current view.
 * Week view: "February 2026"
 * Month view: "February 2026"
 */
function formatDateDisplay(date: Date, _view: CalendarView): string {
  return format(date, 'MMMM yyyy');
}

/**
 * Calendar header with navigation controls.
 * - Left: Prev arrow, Today button, Next arrow
 * - Center: Month/year display
 * - Right: Day | Week | Month toggle
 */
export function CalendarHeader() {
  const visibleStartDate = useCalendarStore((state) => state.visibleStartDate);
  const timeZoneId = useCampaignStore((s) => s.selectedCampaign?.timeZoneId);
  const tzAbbrev = getTimezoneAbbreviation(timeZoneId);
  const currentView = useCalendarStore((state) => state.currentView);
  const navigateWeek = useCalendarStore((state) => state.navigateWeek);
  const navigateMonth = useCalendarStore((state) => state.navigateMonth);
  const goToToday = useCalendarStore((state) => state.goToToday);
  const setView = useCalendarStore((state) => state.setView);
  const isViewingToday = useCalendarStore((state) => state.isViewingToday);

  // Navigate based on current view
  const handlePrev = useCallback(() => {
    if (currentView === 'month') {
      navigateMonth('prev');
    } else {
      navigateWeek('prev');
    }
  }, [currentView, navigateWeek, navigateMonth]);

  const handleNext = useCallback(() => {
    if (currentView === 'month') {
      navigateMonth('next');
    } else {
      navigateWeek('next');
    }
  }, [currentView, navigateWeek, navigateMonth]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputFocused) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    },
    [handlePrev, handleNext]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const dateDisplay = formatDateDisplay(visibleStartDate, currentView);
  const showTodayButton = !isViewingToday();
  const differsFromLocal = isDifferentFromLocal(timeZoneId);

  const handleViewChange = (value: string) => {
    if (value) {
      setView(value as CalendarView);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-200 bg-white">
      {/* Left Section: Navigation */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={handlePrev}
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={goToToday}
          className={showTodayButton ? '' : 'invisible'}
        >
          Today
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleNext}
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Center Section: Date Display */}
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-medium text-foreground">
          {dateDisplay}
        </h2>
        {tzAbbrev && (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded',
              differsFromLocal
                ? 'bg-blue-100 text-blue-700'
                : 'bg-muted text-muted-foreground'
            )}
            title={`All times shown in ${timeZoneId}`}
          >
            <Clock className="h-3 w-3" />
            {tzAbbrev}
          </span>
        )}
      </div>

      {/* Right Section: View Toggle */}
      <ToggleGroup
        type="single"
        value={currentView}
        onValueChange={handleViewChange}
        size="sm"
      >
        <ToggleGroupItem value="week" aria-label="Week view">
          Week
        </ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Month view">
          Month
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}

export default CalendarHeader;
